/**
 * 主线 API。一条链路对应一组接口，不按后端模块拆。
 *
 *   布置：行组 / 行 / 单头
 *   确认：跨季链路候选
 *   执行：授粉事件
 *   追溯：谱系
 */
import dataApi from './api'
import type {
  BreedingPlan,
  BreedingProject,
  DecisionBatchResult,
  DecisionType,
  DecisionWorkbenchResponse,
  FieldPlot,
  HeadStatusSummary,
  LinkCandidate,
  Paged,
  PedigreeResponse,
  PlantHead,
  PlantingRow,
  PollinationEvent,
  PollinationSettleResult,
  ResearchCycle,
  RowGroup,
  ScanResolveResult,
  Track,
  WorkspaceContext,
} from './types'

// ============================================================================
// 季末决策台
// ============================================================================

export function fetchDecisionWorkbench(params: {
  project_id: string
  cycle_id?: string
  plan_id: string
  page?: number
  page_size?: number
  search?: string
  row_group_id?: string
  decision_status?: string
  completeness?: 'ready' | 'blocked'
  lineage_status?: string
  has_field_intention?: boolean
  material_lot_id?: string
  sort_by?: 'harvest_date_desc' | 'lot_code_asc' | 'ready_first'
}) {
  return dataApi.get<DecisionWorkbenchResponse>('/decision-workbench', { params })
}

export interface PostHarvestBlendCandidate {
  material_lot_id: string
  lot_code: string
  display_name?: string | null
  germplasm_id: string
  germplasm_name?: string | null
  generation_label?: string | null
  source_harvest_record_id: string
  harvest_no: string
  head_id: string
  head_code?: string | null
  row_code?: string | null
  available_quantity: number
  quantity_unit: string
}

export interface PostHarvestBlendPayload {
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  output_lot_code?: string
  output_display_name?: string
  notes?: string
  sources: Array<{ source_lot_id: string; quantity: number }>
}

export interface PostHarvestBlendPreview {
  can_confirm: boolean
  blockers: string[]
  warnings: string[]
  germplasm_id?: string | null
  germplasm_name?: string | null
  generation_label?: string | null
  output_quantity: number
  quantity_unit?: string | null
  sources: Array<{
    source_lot_id: string
    lot_code: string
    head_code?: string | null
    quantity: number
    quantity_unit: string
    quantity_before: number
    quantity_after: number
  }>
}

export interface PostHarvestBlendResult {
  id: string
  blend_no: string
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  germplasm_id: string
  output_lot_id: string
  output_lot_code: string
  output_display_name?: string | null
  quantity: number
  quantity_unit: string
  source_count: number
  status: string
  blended_by?: string | null
  blended_at: string
  notes?: string | null
  sources: PostHarvestBlendPreview['sources']
}

export function fetchPostHarvestBlendCandidates(params: { project_id: string; cycle_id: string; plan_id: string }) {
  return dataApi.get<{ total: number; items: PostHarvestBlendCandidate[] }>('/post-harvest-blends/candidates', { params })
}

export function previewPostHarvestBlend(payload: PostHarvestBlendPayload) {
  return dataApi.post<PostHarvestBlendPreview>('/post-harvest-blends/preview', payload)
}

export function createPostHarvestBlend(payload: PostHarvestBlendPayload) {
  return dataApi.post<PostHarvestBlendResult>('/post-harvest-blends', payload)
}

// ============================================================================
// 室内考种 —— 材料批领取、内部补建、复测与审核
// ============================================================================

export type SeedTestStatus = 'draft' | 'in_progress' | 'completed' | 'reviewed' | 'void'

export interface SeedTestCandidate {
  material_lot_id: string
  lot_code: string
  display_name?: string | null
  germplasm_id: string
  germplasm_name: string
  generation_label?: string | null
  source_type: 'harvest' | 'internal_harvest_backfill' | 'post_harvest_blend' | string
  is_bulk_material: boolean
  source_harvest_record_id?: string | null
  harvest_no?: string | null
  target_kind?: string | null
  source_entity_code?: string | null
  row_code?: string | null
  group_code?: string | null
  external_provider_name?: string | null
  external_lot_code?: string | null
  quantity?: number | null
  quantity_unit?: string | null
  latest_round_no: number
  latest_status?: SeedTestStatus | null
}

export interface SeedTestTraitValue {
  id?: string
  trait_code: string
  trait_name?: string | null
  value_type?: string | null
  unit?: string | null
  value_text?: string | null
  value_number?: number | null
  value_code?: string | null
  value_json?: unknown
  is_missing?: boolean
  missing_reason?: string | null
  sort_order?: number
}

export interface SeedTestRecord {
  id: string
  test_no: string
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  material_lot_id: string
  lot_code: string
  germplasm_id: string
  germplasm_name: string
  generation_label?: string | null
  source_harvest_record_id?: string | null
  round_no: number
  supersedes_record_id?: string | null
  status: SeedTestStatus
  tested_by?: string | null
  tested_at: string
  completed_at?: string | null
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_comment?: string | null
  notes?: string | null
  traits: SeedTestTraitValue[]
  created_at: string
}

export interface ResolvedSeedTestTrait {
  trait_code: string
  trait_name: string
  category?: string | null
  value_type: 'number' | 'code' | 'text' | 'date' | 'bool' | string
  measure_kind: string
  unit?: string | null
  default_scope: string
  enum_options: Array<{ value: string; label: string }>
  frequent_values: Array<string | number>
  input_kind: string
  input_configured: boolean
  input_issue?: string | null
  sort_order: number
}

export function fetchSeedTestCandidates(params: {
  project_id: string
  cycle_id: string
  plan_id: string
  keyword?: string
}) {
  return dataApi.get<{ total: number; items: SeedTestCandidate[] }>('/seed-tests/candidates', { params })
}

export function fetchSeedTestRecords(params: {
  project_id: string
  cycle_id: string
  plan_id: string
  material_lot_id?: string
  status?: SeedTestStatus
}) {
  return dataApi.get<{ total: number; items: SeedTestRecord[] }>('/seed-tests', { params })
}

export function fetchSeedTestTraits(params: {
  crop_code?: string
  project_id?: string
  germplasm_id?: string
}) {
  return dataApi.get<{ traits: ResolvedSeedTestTrait[] }>('/trait-definitions/resolve', {
    params: { domain: 'harvest', ...params },
  })
}

export function createSeedTest(payload: {
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  material_lot_id: string
  status: 'draft' | 'in_progress' | 'completed'
  supersedes_record_id?: string
  notes?: string
  traits: SeedTestTraitValue[]
}) {
  return dataApi.post<SeedTestRecord>('/seed-tests', payload)
}

export function updateSeedTest(recordId: string, payload: {
  status?: 'draft' | 'in_progress' | 'completed'
  notes?: string
  traits?: SeedTestTraitValue[]
}) {
  return dataApi.patch<SeedTestRecord>(`/seed-tests/${recordId}`, payload)
}

export function reviewSeedTest(recordId: string, review_comment?: string) {
  return dataApi.post<SeedTestRecord>(`/seed-tests/${recordId}/review`, { review_comment })
}

export function voidSeedTest(recordId: string, reason: string) {
  return dataApi.post<SeedTestRecord>(`/seed-tests/${recordId}/void`, { reason })
}

export function createInternalHarvestBackfill(payload: {
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  target_kind: 'head' | 'row' | 'group'
  source_entity_id: string
  reason: string
  harvest_date: string
  harvest_quantity?: number
  harvest_unit: 'g' | 'kg'
  output_lot_code?: string
  output_display_name?: string
  evidence_payload?: Record<string, unknown>
  notes?: string
}) {
  return dataApi.post<{
    id: string
    harvest_record_id: string
    output_lot_id: string
    output_lot_code: string
  }>('/seed-tests/internal-backfills', payload)
}

// ============================================================================
// 外部材料 —— 独立接收、身份审核和外部考种
// ============================================================================

export type ExternalReceiptStatus = 'pending' | 'accepted' | 'rejected' | 'void'

export interface ExternalMaterialReceipt {
  id: string
  receipt_no: string
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  provider_name: string
  provider_type: string
  provider_contact?: string | null
  external_lot_code: string
  external_material_name: string
  received_date: string
  received_quantity?: number | null
  quantity_unit: string
  origin_location?: string | null
  storage_location?: string | null
  generation_label?: string | null
  evidence_payload?: Record<string, unknown> | null
  acceptance_notes?: string | null
  germplasm_id?: string | null
  germplasm_code?: string | null
  germplasm_name?: string | null
  output_lot_id?: string | null
  output_lot_code?: string | null
  identity_mode: string
  status: ExternalReceiptStatus
  submitted_by?: string | null
  submitted_at: string
  reviewed_by?: string | null
  reviewed_at?: string | null
  review_comment?: string | null
}

export function fetchExternalMaterialReceipts(params: {
  project_id: string
  cycle_id: string
  plan_id: string
  status?: ExternalReceiptStatus
  keyword?: string
}) {
  return dataApi.get<{ total: number; items: ExternalMaterialReceipt[] }>('/external-materials', { params })
}

export function createExternalMaterialReceipt(payload: {
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  provider_name: string
  provider_type: string
  provider_contact?: string
  external_lot_code: string
  external_material_name: string
  received_date: string
  received_quantity?: number
  quantity_unit: string
  origin_location?: string
  storage_location?: string
  generation_label?: string
  evidence_payload?: Record<string, unknown>
  acceptance_notes?: string
}) {
  return dataApi.post<ExternalMaterialReceipt>('/external-materials', payload)
}

export function acceptExternalMaterialReceipt(receiptId: string, payload: {
  identity_mode: 'existing' | 'create'
  existing_germplasm_id?: string
  new_germplasm_code?: string
  new_germplasm_name?: string
  species?: string
  crop_type?: string
  storage_method?: string
  resource_category?: string
  output_lot_code?: string
  output_display_name?: string
  review_comment?: string
}) {
  return dataApi.post<ExternalMaterialReceipt>(`/external-materials/${receiptId}/accept`, payload)
}

export function rejectExternalMaterialReceipt(receiptId: string, reason: string) {
  return dataApi.post<ExternalMaterialReceipt>(`/external-materials/${receiptId}/reject`, { reason })
}

export function fetchExternalSeedTestCandidates(params: {
  project_id: string
  cycle_id: string
  plan_id: string
  keyword?: string
}) {
  return dataApi.get<{ total: number; items: SeedTestCandidate[] }>('/external-materials/seed-test-candidates', { params })
}

export function createExternalSeedTest(payload: {
  request_id: string
  project_id: string
  cycle_id: string
  plan_id: string
  material_lot_id: string
  status: 'draft' | 'in_progress' | 'completed'
  supersedes_record_id?: string
  notes?: string
  traits: SeedTestTraitValue[]
}) {
  return dataApi.post<SeedTestRecord>('/external-materials/seed-tests', payload)
}

export function createDecisionDraftsBatch(payload: {
  project_id: string
  cycle_id?: string
  plan_id: string
  items: Array<{
    material_lot_id: string
    decision_type: DecisionType
    decision_reason: string
    outcome_payload?: unknown
    supersedes_decision_id?: string
  }>
}) {
  return dataApi.post<DecisionBatchResult>('/decision-events/batch-drafts', payload)
}

export function submitDecisionBatch(decision_event_ids: string[]) {
  return dataApi.post<DecisionBatchResult>('/decision-events/batch-submit', {
    decision_event_ids,
  })
}

export function approveDecision(decisionEventId: string, review_comment?: string) {
  return dataApi.post<import('./types').DecisionEvent>(
    `/decision-events/${decisionEventId}/approve`,
    { review_comment },
  )
}

export function rejectDecision(decisionEventId: string, review_comment?: string) {
  return dataApi.post<import('./types').DecisionEvent>(
    `/decision-events/${decisionEventId}/reject`,
    { review_comment },
  )
}

export interface DecisionExecutionPreview {
  decision_event_id: string
  decision_type: string
  material_lot_id: string
  can_execute: boolean
  existing_status?: string | null
  actions: Array<Record<string, unknown>>
  blockers: string[]
}

export function previewDecisionExecution(decisionEventId: string) {
  return dataApi.get<DecisionExecutionPreview>(
    `/decision-events/${decisionEventId}/execution-preview`,
  )
}

export function executeDecision(decisionEventId: string) {
  return dataApi.post<{ id: string; status: string; result_message?: string; result_payload?: unknown }>(
    `/decision-events/${decisionEventId}/execute`,
    {},
  )
}

export interface NextSeasonPlanningPayload {
  target_plan_id: string
  allocation_id?: string
  plot_id?: string
  row_prefix: string
  start_row_number?: number
  row_count: number
  intended_use?: string
  target_output?: string
  planned_date?: string
  notes?: string
  create_task: boolean
  task_title?: string
  task_priority: 'low' | 'medium' | 'high'
  task_due_at?: string
}

export interface NextSeasonPlanningPreview {
  decision_event_id: string
  candidate_id: string
  source_material_lot_id: string
  source_material_lot_code: string
  from_plan_id: string
  from_plan_name: string
  target_plan_id: string
  target_plan_name: string
  target_cycle_id?: string | null
  target_cycle_name?: string | null
  station_id: string
  station_name?: string | null
  plot_id?: string | null
  plot_name?: string | null
  intended_use?: string | null
  row_group_code: string
  primary_row_code: string
  row_codes: string[]
  task_title?: string | null
  task_priority?: string | null
  can_confirm: boolean
  blockers: string[]
  already_confirmed: boolean
  existing_row_group_id?: string | null
  allocation_id?: string | null
}

export interface NextSeasonPlanningResult {
  decision_event_id: string
  candidate_id: string
  carry_forward_relation_id: string
  row_group_id: string
  row_group_code: string
  row_ids: string[]
  row_codes: string[]
  task_id?: string | null
  task_code?: string | null
  already_confirmed: boolean
}

export function previewNextSeasonPlanning(
  decisionEventId: string,
  payload: NextSeasonPlanningPayload,
) {
  return dataApi.post<NextSeasonPlanningPreview>(
    `/decision-events/${decisionEventId}/next-season-preview`,
    payload,
  )
}

export function confirmNextSeasonPlanning(
  decisionEventId: string,
  payload: NextSeasonPlanningPayload,
) {
  return dataApi.post<NextSeasonPlanningResult>(
    `/decision-events/${decisionEventId}/confirm-next-season-plan`,
    payload,
  )
}

export type PlanAllocationType = 'research' | 'multiplication' | 'comparison' | 'multi_site'
export type PlanAllocationLayoutMode = 'row_numbered' | 'plot_only'

export interface PlanMaterialAllocation {
  id: string
  allocation_no: string
  source_material_lot_id: string
  source_lot_code: string
  source_material_name?: string | null
  source_decision_id?: string | null
  source_candidate_id?: string | null
  target_plan_id: string
  target_plan_code: string
  target_plan_name: string
  target_plan_status?: string | null
  target_cycle_id?: string | null
  source_generation_label?: string | null
  planting_generation_label?: string | null
  expected_output_generation_label?: string | null
  station_id: string
  station_name?: string | null
  plot_id?: string | null
  plot_name?: string | null
  allocation_type: PlanAllocationType
  layout_mode: PlanAllocationLayoutMode
  reserved_quantity: number
  quantity_unit: string
  row_spacing_cm?: number | null
  plant_spacing_cm?: number | null
  row_length_m?: number | null
  row_count?: number | null
  plot_count: number
  replicate_count: number
  plants_per_row?: number | null
  planned_plant_count?: number | null
  planned_area_m2?: number | null
  status: string
  allocated_at: string
  row_group_id?: string | null
  committed_at?: string | null
  release_reason?: string | null
  notes?: string | null
}

export interface MaterialAllocationAvailability {
  material_lot_id: string
  lot_code: string
  quantity?: number | null
  quantity_unit: string
  reserved_quantity: number
  available_quantity?: number | null
  allocation_count: number
}

export interface PlanMaterialAllocationPayload {
  request_id: string
  source_material_lot_id: string
  target_plan_id: string
  planting_generation_label?: string
  expected_output_generation_label?: string
  source_decision_id?: string
  source_candidate_id?: string
  allocation_type: PlanAllocationType
  layout_mode: PlanAllocationLayoutMode
  reserved_quantity: number
  quantity_unit: string
  plot_id?: string
  row_spacing_cm?: number
  plant_spacing_cm?: number
  row_length_m?: number
  row_count?: number
  plot_count: number
  replicate_count: number
  notes?: string
}

export function fetchPlanMaterialAllocations(params: { source_material_lot_id?: string; source_plan_id?: string; target_plan_id?: string; status?: string }) {
  return dataApi.get<{ total: number; items: PlanMaterialAllocation[] }>('/plan-material-allocations', { params })
}

export function fetchMaterialAllocationAvailability(materialLotId: string) {
  return dataApi.get<MaterialAllocationAvailability>(`/plan-material-allocations/availability/${materialLotId}`)
}

export function createPlanMaterialAllocation(payload: PlanMaterialAllocationPayload) {
  return dataApi.post<PlanMaterialAllocation>('/plan-material-allocations', payload)
}

export function releasePlanMaterialAllocation(allocationId: string, reason: string) {
  return dataApi.post<PlanMaterialAllocation>(`/plan-material-allocations/${allocationId}/release`, { reason })
}

export interface QuickPlanDraftPayload {
  project_id: string
  cycle_id: string
  station_id: string
  plan_name: string
  plan_type: PlanAllocationType
  responsible_person?: string
  breeding_objective?: string
  parent_plan_id?: string
  default_row_spacing_cm?: number
  default_plant_spacing_cm?: number
  default_row_length_m?: number
  notes?: string
}

export function createQuickPlanDraft(payload: QuickPlanDraftPayload) {
  return dataApi.post<BreedingPlan>('/breeding-plan/quick-draft', payload)
}

export function confirmBreedingPlan(planId: string) {
  return dataApi.post<BreedingPlan>(`/breeding-plan/${planId}/confirm`)
}

// ============================================================================
// 驾驶舱
// ============================================================================

export function fetchWorkspace(params: {
  project_id: string
  cycle_id?: string
  plan_id?: string
  recent_limit?: number
}) {
  return dataApi.get<WorkspaceContext>('/research-chain/workspace', { params })
}

// ============================================================================
// 项目 / 季 / 计划
// ============================================================================

export function fetchProjects() {
  return dataApi.get<{ total: number; items: BreedingProject[] }>('/breeding-projects')
}

export function fetchCycles(params?: { project_id?: string }) {
  return dataApi.get<{ total: number; items: ResearchCycle[] }>('/research-cycles', { params })
}

export interface PlanQuery {
  project_id?: string
  cycle_id?: string
  skip?: number
  limit?: number
}

export function fetchPlans(params?: PlanQuery) {
  return dataApi.get<{ total: number; items: BreedingPlan[] }>('/breeding-plan', { params })
}

/**
 * 取当前授权范围内的全部计划书。
 *
 * `/breeding-plan` 默认只回 10 条；顶栏需要跨季切换，不能把“第一页”误当成
 * “全部计划”。逐页读取也避免项目积累超过单页上限 200 后再次漏掉老季或新季。
 */
export async function fetchAllPlans(params?: Omit<PlanQuery, 'skip' | 'limit'>) {
  const limit = 200
  let skip = 0
  const items: BreedingPlan[] = []

  while (true) {
    const page = await fetchPlans({ ...params, skip, limit })
    items.push(...(page.items ?? []))
    if (items.length >= page.total || (page.items ?? []).length === 0) {
      return { total: page.total, items }
    }
    skip += page.items.length
  }
}

// ── 新建一季 ──
// 三级都要有，缺一级后面的行组就没处挂。
// 走后端 /open-season 一个事务建齐：分三个请求时中途失败会留半截数据
// （周期建了、计划书没建），季次码被占住，重试永远报「已存在」。

export function fetchStations() {
  return dataApi.get<{ total: number; items: Array<{ id: string; station_name: string }> }>(
    '/field-plots/stations',
  )
}

export function fetchFieldPlots(params?: {
  skip?: number
  limit?: number
  status?: string
  search?: string
  station_id?: string
}) {
  return dataApi.get<Paged<FieldPlot>>('/field-plots/', { params })
}

export function fetchNextProjectCode(params: {
  crop_code: string
  station_id: string
  year?: number
}) {
  return dataApi.get<{ code: string }>('/breeding-projects-next-code', { params })
}

export function createProject(payload: {
  project_code?: string
  crop_code: string
  project_name: string
  project_detail?: string
  color_hex: string
  station_id: string
}) {
  return dataApi.post<BreedingProject>('/breeding-projects', payload)
}

export function createCycle(payload: {
  project_id: string
  cycle_code: string
  cycle_name: string
  year: number
  season_type: string
  station_group?: string
  notes?: string
}) {
  return dataApi.post<ResearchCycle>('/research-cycles', payload)
}

export function fetchNextPlanCode(params: { project_id: string }) {
  return dataApi.get<{ code: string }>('/breeding-plan-next-code', { params })
}

export interface OpenSeasonPayload {
  /** 给了就用已有项目，否则按 project_name 新建（同名复用） */
  project_id?: string
  project_name?: string
  project_code?: string
  crop_code?: string
  color_hex?: string
  project_detail?: string
  /** 沿用已有项目时可省，后端从项目继承 */
  station_id?: string
  year: number
  season_type: 'summer' | 'winter'
  plan_name: string
  breeding_objective: string
}

export interface OpenSeasonResult {
  project_id: string
  project_name: string
  cycle_id: string
  cycle_code: string
  cycle_name: string
  plan_id: string
  plan_code: string
  plan_name: string
  /** 6 位季次编码，如 202602 */
  season_code: string
  /** 行号前缀，如 262 */
  row_prefix: string
  /** 复用了哪几级：project / cycle / plan */
  reused: string[]
}

/** 开一季 —— 项目 + 周期 + 计划书在一个事务里建齐。 */
export function openSeason(payload: OpenSeasonPayload) {
  return dataApi.post<OpenSeasonResult>('/open-season', payload)
}

export function createPlan(payload: {
  plan_code?: string
  plan_name: string
  project_id: string
  cycle_id?: string
  station_id?: string
  plan_year: number
  breeding_objective?: string
}) {
  return dataApi.post<BreedingPlan>('/breeding-plan', payload)
}

// ============================================================================
// 行组 —— 计划书的编写单位
// ============================================================================

export interface RowGroupQuery {
  plan_id?: string
  cycle_id?: string
  plot_id?: string
  track?: Track
  row_kind?: string
  /** 行号命名空间，如 T / Y / KT */
  row_prefix?: string
  /** 按组编号 / 行号区间 / 材料名搜索 */
  keyword?: string
  page?: number
  page_size?: number
}

export function fetchRowGroups(params: RowGroupQuery) {
  return dataApi.get<Paged<RowGroup>>('/row-groups', { params })
}

export function fetchRowGroup(groupId: string) {
  return dataApi.get<RowGroup>(`/row-groups/${groupId}`)
}

/** 改行组。材料改一次，组内所有行跟着变——这是行组存在的意义。 */
export function updateRowGroup(groupId: string, payload: Partial<RowGroup>) {
  return dataApi.put<RowGroup>(`/row-groups/${groupId}`, payload)
}

export function createRowGroup(payload: Partial<RowGroup>) {
  return dataApi.post<RowGroup>('/row-groups', payload)
}

/**
 * 扫上一季的牌，取它产出的那批种子。
 *
 * 建本季行组时用：人手上拿的是上一季插在地里的牌（或收进袋子那张），
 * 扫一下就知道该填哪个 `material_lot_id`，不用回头翻采收台账。
 * 拿不到材料批也返回 200 —— 拿不到的原因本身就是要显示给人的信息。
 */
export function scanResolve(tagCode: string) {
  return dataApi.get<ScanResolveResult>(`/scan-resolve/${encodeURIComponent(tagCode)}`)
}

// ============================================================================
// 种植行 —— 地插行号
// ============================================================================

export interface PlantingRowQuery {
  group_id?: string
  plan_id?: string
  cycle_id?: string
  plot_id?: string
  track?: Track
  /** 行号命名空间，如 T / Y / KT */
  row_prefix?: string
  row_status?: string
  /** 行号区间，用户说「T100 到 T200」时用这个 */
  row_number_from?: number
  row_number_to?: number
  keyword?: string
  page?: number
  page_size?: number
}

export function fetchPlantingRows(params: PlantingRowQuery) {
  return dataApi.get<Paged<PlantingRow>>('/planting-rows', { params })
}

export function fetchPlantingRow(rowId: string) {
  return dataApi.get<PlantingRow>(`/planting-rows/${rowId}`)
}

export function updatePlantingRow(rowId: string, payload: Partial<PlantingRow>) {
  return dataApi.put<PlantingRow>(`/planting-rows/${rowId}`, payload)
}

/** 按行号区间批量建行。计划书写 T100-T103，一次建 4 行。 */
export function batchCreateRows(payload: {
  group_id: string
  row_prefix: string
  start_number: number
  end_number: number
  season_code?: string
  row_type?: string
  plot_id?: string
  plant_count?: number
}) {
  return dataApi.post<Paged<PlantingRow>>('/planting-rows/batch', payload)
}

export function fetchRowHeadSummary(rowId: string) {
  return dataApi.get<HeadStatusSummary>(`/planting-rows/${rowId}/head-summary`)
}

// ============================================================================
// 单头
// ============================================================================

export function fetchPlantHeads(params: {
  row_id?: string
  group_id?: string
  plan_id?: string
  head_status?: string
  keyword?: string
  page?: number
  page_size?: number
}) {
  return dataApi.get<Paged<PlantHead>>('/plant-heads', { params })
}

/** 按行批量建单头。 */
export function batchCreateHeads(payload: {
  row_id: string
  count: number
  start_number?: number
  source?: string
  operator?: string
}) {
  return dataApi.post<Paged<PlantHead>>('/plant-heads/batch', payload)
}

export function fetchHeadPedigree(headId: string) {
  return dataApi.get<{ location: Record<string, unknown>; ancestors: PedigreeResponse | null }>(
    `/plant-heads/${headId}/pedigree`,
  )
}

// ============================================================================
// 追溯 —— 扫码或输行号
// ============================================================================

/** 依次尝试：标签码 → 单头编号 → 行号 → 行组编号 → 材料批编号。 */
export function traceByCode(code: string) {
  return dataApi.get<Record<string, unknown>>(`/trace/${encodeURIComponent(code)}`)
}

export function fetchLotDescendants(lotId: string, maxDepth = 5) {
  return dataApi.get<PedigreeResponse>(`/material-lots/${lotId}/descendants`, {
    params: { max_depth: maxDepth },
  })
}

export function fetchMaterialGraph(lotId: string) {
  return dataApi.get<Record<string, unknown>>(`/research-chain/materials/${lotId}/graph`)
}

// ============================================================================
// 跨季链路候选 —— 确认队列
// ============================================================================

export function fetchLinkCandidates(params: {
  project_id?: string
  cycle_id?: string
  plan_id?: string
  status?: string
  candidate_type?: string
  evidence_level?: string
  page?: number
  page_size?: number
}) {
  return dataApi.get<Paged<LinkCandidate>>('/research-link-candidates', { params })
}

/**
 * 确认一条链路。这是写谱系的两个手工入口之一（另一个是授粉结算）。
 * 确认后写 MaterialLineageRelation(parent_lot → child_lot)。
 */
export function confirmLinkCandidate(
  candidateId: string,
  payload: {
    confirmed_parent_code?: string
    confirmed_target_type?: string
    confirmed_target_id?: string
    review_notes?: string
  },
) {
  return dataApi.post<{
    candidate_id: string
    status: string
    lineage_relation_id?: string | null
    message?: string
  }>(`/research-link-candidates/${candidateId}/confirm`, payload)
}

/** 标为外部来源：不入谱系，但记下已判断过。 */
export function rejectLinkCandidate(candidateId: string, payload: { review_notes?: string }) {
  return dataApi.post<{ candidate_id: string; status: string }>(
    `/research-link-candidates/${candidateId}/reject`,
    payload,
  )
}

// ============================================================================
// 授粉事件
// ============================================================================

export function fetchPollinationEvents(params: {
  plan_id?: string
  cycle_id?: string
  female_row_id?: string
  status?: string
  lineage_status?: string
  page?: number
  page_size?: number
}) {
  return dataApi.get<Paged<PollinationEvent>>('/pollination/events', { params })
}

/** 按行组批量排期：「T100-T103 授 T200 的粉」一次生成 4 条。 */
export function batchPlanPollination(payload: {
  female_group_id: string
  donor_expression_raw: string
  donors?: Array<{
    donor_kind: string
    donor_row_id?: string
    donor_row_code?: string
    donor_expression_raw?: string
  }>
  emasculated?: boolean
  pollination_mode?: string
  planned_date?: string
  per_head?: boolean
}) {
  return dataApi.post<PollinationEvent[]>('/pollination/events/batch-plan', payload)
}

export function settlePollination(eventId: string, payload?: { settled_by?: string; force?: boolean }) {
  return dataApi.post<PollinationSettleResult>(`/pollination/events/${eventId}/settle`, payload ?? {})
}

export function settlePollinationBatch(params: { plan_id?: string; cycle_id?: string; limit?: number }) {
  return dataApi.post<PollinationSettleResult[]>('/pollination/settle-batch', null, { params })
}

/** 待结算队列：已执行但谱系还没落定。 */
export function fetchPendingSettlement(params: {
  plan_id?: string
  cycle_id?: string
  page?: number
  page_size?: number
}) {
  return dataApi.get<Paged<PollinationEvent>>('/pollination/pending-settlement', { params })
}

// ============================================================================
// 本季采收批次
// ============================================================================

export interface SeasonHarvestBatch {
  id: string
  harvest_no: string
  harvest_code?: string | null
  target_kind: 'head' | 'row' | 'group' | string
  sequence_number?: number | null
  harvest_date: string
  harvest_quantity?: number | null
  harvest_unit?: string | null
  generation_label?: string | null
  remarks?: string | null
  row_code?: string | null
  row_prefix?: string | null
  row_number?: number | null
  range_label?: string | null
  material_name_raw?: string | null
  source_description?: string | null
  child_lot_id?: string | null
  child_lot_code?: string | null
  child_lot_decision_status?: string | null
  trait_count: number
  traits: Array<{
    trait_code: string
    trait_name: string
    value_text?: string | null
    value_number?: number | null
    value_code?: string | null
    unit?: string | null
    media_count?: number
    media_ids?: string[]
    is_missing?: boolean
    missing_reason?: string | null
  }>
}

export function fetchSeasonHarvestBatches(params: {
  plan_id: string
  cycle_id?: string
  keyword?: string
  row_prefix?: string
  target_kind?: string
  date_start?: string
  date_end?: string
  page?: number
  page_size?: number
}) {
  return dataApi.get<Paged<SeasonHarvestBatch>>('/harvest/records', { params })
}

export function fetchSeasonHarvestRecord(harvestId: string) {
  return dataApi.get<SeasonHarvestBatch>(`/harvest/records/${harvestId}`)
}
